// SubtitleSelector.jsx
import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import ReactDOM from "react-dom";
import { useSelectedSubtitles } from "../../../context/SelectedSubtitlesContext";
import { useVideo } from "../../../context/VideoContext";
import useVideoManager from "../../../hooks/useVideoManager";
import SubtitleSearchModal from "../../subtitle/SubtitleSearchModal";
import "./SubtitleSelector.css";
import { fetchSubtitleContent } from "../../../services/subtitleApi";
import { convertCuesToSRT } from "../../../utils/formatTime";
import {
  getSubtitlesByMovie,
  updateSubtitleUsage,
} from "../../../services/subtitleStorageService";
import SubtitleUploadModal from "../SubtitleUploadModal";
import { formatTime } from "../../../utils/formatTime";
import EmbeddedSubtitlesModal from "./EmbeddedSubtitlesModal";
import { getPortalContainer } from "../../../utils/portalContainer";

import { getLanguageColor, getLanguageName } from "../../../utils/languages";

const SubtitleSelector = ({
  onClose,
  handleCollectionSearchClick,
  handleComparisonClick,
}) => {
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [movieSubtitles, setMovieSubtitles] = useState([]);
  const fileInputRef = useRef(null);
  const [uploadModalFiles, setUploadModalFiles] = useState(null);

  const { subtitleHistory } = useSelectedSubtitles();
  const { subtitle1Loaded, subtitle2Loaded, videoMetadata, isFullscreen, isIOS } =
    useVideo();
  const { loadLocalSubtitle } = useVideoManager();
  const [showEmbeddedModal, setShowEmbeddedModal] = useState(false);

  // ✨ تشخیص موبایل
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 480);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // حالت انتخاب زیرنویس‌ها
  const [selectedForDownload, setSelectedForDownload] = useState({
    1: null,
    2: null,
  });

  // ========== تفکیک زیرنویس‌ها بر اساس منبع ==========
  const { embeddedSubtitles, uploadedSubtitles } = useMemo(() => {
    const embedded = [];
    const uploaded = [];

    movieSubtitles.forEach((sub) => {
      if (
        sub.source === "embedded" ||
        sub.fileName?.startsWith("embedded_subtitle_") ||
        sub.fileName?.includes("_embedded_")
      ) {
        embedded.push(sub);
      } else {
        uploaded.push(sub);
      }
    });

    return { embeddedSubtitles: embedded, uploadedSubtitles: uploaded };
  }, [movieSubtitles]);

  // لود زیرنویس‌های مرتبط با این فیلم
  useEffect(() => {
    if (videoMetadata?.fileName) {
      loadMovieSubtitles();
    }
  }, [videoMetadata?.fileName]);

  const loadMovieSubtitles = async () => {
    const subtitles = await getSubtitlesByMovie(videoMetadata.fileName);
    setMovieSubtitles(subtitles);
  };

  // ✨ تغییر: کلیک روی بک‌دراپ نه مودال رو میبنده نه روی ویدیو اثر میذاره
  const handleOverlayClick = (e) => {
    e.stopPropagation();
    // هیچ کاری انجام نده - مودال باز می‌مونه و کلیک به ویدیو نمیرسه
  };

  const handleSearchClick = () => {
    setShowSearchModal(true);
  };

  const handleSearchModalClose = (shouldCloseSelector = false) => {
    setShowSearchModal(false);
    if (shouldCloseSelector) {
      onClose();
    }
  };

  // انتخاب زیرنویس برای شماره ۱ یا ۲
  const handleSelectSubtitle = (subtitle, targetNumber) => {
    const subtitleId = subtitle.id || subtitle._id;

    setSelectedForDownload((prev) => {
      if (
        prev[targetNumber] &&
        (prev[targetNumber].id === subtitleId ||
          prev[targetNumber]._id === subtitleId)
      ) {
        return { ...prev, [targetNumber]: null };
      }

      return {
        ...prev,
        [targetNumber]: subtitle,
      };
    });
  };

  // بررسی اینکه آیا زیرنویس برای شماره خاصی انتخاب شده
  const isSubtitleSelected = (subtitle, targetNumber) => {
    const selected = selectedForDownload[targetNumber];
    if (!selected) return false;

    return (
      selected.id === (subtitle.id || subtitle._id) ||
      selected._id === (subtitle.id || subtitle._id)
    );
  };

  const buildSubMetadata = useCallback((sub) => ({
    sourceType: sub.sourceType || sub.source || (sub.onlineId ? "online" : "local"),
    onlineId: sub.onlineId || sub.subtitlesUploadId || null,
    subtitlesUploadId: sub.subtitlesUploadId || sub.onlineId || null,
    language: sub.language || sub.subtitlesLanguage || "unknown",
    nameOfMovie: sub.nameOfMovie || sub.movieName || "",
    movieName: sub.movieName || sub.nameOfMovie || "",
    videoType: sub.videoType || "movie",
    season: sub.season?.toString() || "",
    episode: sub.episode?.toString() || "",
    fileName: sub.fileName || "",
    coverUrl: sub.coverUrl || "",
    otherTags: sub.otherTags || [],
    productionYear: sub.productionYear || "",
    _source: "history_reload",
  }), []);

  const handleDownloadSelected = async () => {
    const subsToDownload = [];
    if (selectedForDownload[1]) {
      subsToDownload.push({ ...selectedForDownload[1], targetNumber: 1 });
    }
    if (selectedForDownload[2]) {
      subsToDownload.push({ ...selectedForDownload[2], targetNumber: 2 });
    }

    if (subsToDownload.length === 0) return;

    setDownloading(true);
    try {
      for (const sub of subsToDownload) {
        const lang = sub.language || sub.subtitlesLanguage || "unknown";

        if (
          sub.source === "local" ||
          sub.source === "embedded" ||
          sub.source === "temp_memory"
        ) {
          try {
            if (sub.cues && Array.isArray(sub.cues) && sub.cues.length > 0) {
              const srtContent = convertCuesToSRT(sub.cues);
              const file = new File(
                [srtContent],
                sub.fileName || `subtitle_${sub.id}.srt`,
                {
                  type: "text/plain",
                },
              );
              await loadLocalSubtitle(file, lang, sub.targetNumber, buildSubMetadata(sub));
            } else if (sub.rawContent) {
              const file = new File(
                [sub.rawContent],
                sub.fileName || `subtitle_${sub.id}.srt`,
                {
                  type: "text/plain",
                },
              );
              await loadLocalSubtitle(file, lang, sub.targetNumber, buildSubMetadata(sub));
            } else if (sub.subtitleText) {
              const file = new File(
                [sub.subtitleText],
                `subtitle_${sub.id}.srt`,
                {
                  type: "text/plain",
                },
              );
              await loadLocalSubtitle(file, lang, sub.targetNumber, buildSubMetadata(sub));
            } else {
              console.error("No valid subtitle data found!", sub);
            }
          } catch (error) {
            console.error("Error loading local subtitle:", error);
          }
        } else if (sub.onlineId || sub.subtitlesUploadId || sub._id) {
          try {
            const content = await fetchSubtitleContent(
              sub.onlineId || sub.subtitlesUploadId || sub._id,
            );
            const file = new File(
              [content],
              `${sub.nameOfMovie || "subtitle"}_${
                sub.subtitlesLanguage || sub.language
              }_${sub.targetNumber}.srt`,
              { type: "text/plain" },
            );
            await loadLocalSubtitle(file, lang, sub.targetNumber, buildSubMetadata(sub));
          } catch (error) {
            console.error("Error fetching online subtitle:", error);
          }
        } else {
          console.error("Unknown subtitle source:", sub);
        }
      }

      setSelectedForDownload({ 1: null, 2: null });
      setTimeout(() => onClose(), 500);
    } catch (error) {
      console.error("Error downloading subtitles:", error);
    } finally {
      setDownloading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploadModalFiles(files);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUploadConfirm = async (config) => {
    setUploading(true);
    try {
      for (const item of config) {
        await loadLocalSubtitle(item.file, item.language, item.subtitleNumber);
      }

      setUploadModalFiles(null);

      setTimeout(() => onClose(), 500);
    } catch (error) {
      console.error("Error uploading subtitles:", error);
    } finally {
      setUploading(false);
    }
  };

  const renderSubtitleItem = (sub, isOnline = false) => {
    const langCode = isOnline ? sub.subtitlesLanguage : sub.language;
    const langColor = getLanguageColor(langCode);
    const id = isOnline ? sub._id?.slice(-6) : sub.id?.slice(-6) || "LOCAL";
    const name = isOnline ? sub.nameOfMovie : sub.fileName;
    const showEpisode = isOnline && sub.season && sub.episode;

    return (
      <div
        key={
          isOnline ? `${sub._id}-${sub.targetNumber || Math.random()}` : sub.id
        }
        className="history-item"
      >
        <div className="history-item-info">
          <div className="history-item-main">
            <span
              className="history-item-language"
              style={{
                backgroundColor: langColor.bg,
                color: langColor.text,
              }}
            >
              {getLanguageName(langCode)}
            </span>
            {!isMobile && <span className="history-item-id">{id}</span>}
            <span className="history-item-name" title={name}>
              {name}
              {showEpisode && (
                <span className="history-item-episode">
                  S{sub.season}E{sub.episode}
                </span>
              )}
            </span>
            {!isOnline && sub.addedAt && !isMobile && (
              <span className="history-item-date">
                {new Date(sub.addedAt).toLocaleDateString("fa-IR")}
              </span>
            )}
          </div>
        </div>

        <div className="history-item-actions">
          <button
            className={`history-item-btn btn-1 ${
              isSubtitleSelected(sub, 1) ? "selected" : ""
            }`}
            onClick={() => handleSelectSubtitle(sub, 1)}
            disabled={isSubtitleSelected(sub, 2) || downloading}
          >
            {isSubtitleSelected(sub, 1) ? "✓" : "۱"}
          </button>

          <button
            className={`history-item-btn btn-2 ${
              isSubtitleSelected(sub, 2) ? "selected" : ""
            }`}
            onClick={() => handleSelectSubtitle(sub, 2)}
            disabled={isSubtitleSelected(sub, 1) || downloading}
          >
            {isSubtitleSelected(sub, 2) ? "✓" : "۲"}
          </button>
        </div>
      </div>
    );
  };

  const totalCount =
    subtitleHistory.length +
    embeddedSubtitles.length +
    uploadedSubtitles.length;

  return ReactDOM.createPortal(
    <>
      <div className="subtitle-selector-overlay" onClick={handleOverlayClick}>
        <div
          className={`subtitle-selector-panel ${isMobile ? "ss-mobile" : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="subtitle-selector-header">
            <h3>انتخاب زیرنویس</h3>
            <button className="close-button" onClick={onClose}>
              ✕
            </button>
          </div>

          <div className="subtitle-selector-content">
            {/* ========== گروه اول: دکمه‌های اصلی ========== */}
            <div className="action-group primary-group">
              {!isMobile && (
                <div className="group-label">روش‌های دریافت زیرنویس</div>
              )}
              <div className="selector-actions">
                <button
                  className="action-button search-button"
                  onClick={handleSearchClick}
                >
                  <span className="action-icon">🔍</span>
                  <span className="action-text">جستجوی زیرنویس</span>
                </button>

                <button
                  className="action-button upload-button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  <span className="action-icon">📁</span>
                  <span className="action-text">
                    {uploading ? "در حال آپلود..." : "انتخاب فایل"}
                  </span>
                </button>

                <button
                  className="action-button embedded-button"
                  onClick={() => setShowEmbeddedModal(true)}
                >
                  <span className="action-icon">🎬</span>
                  <span className="action-text">زیرنویس‌های فیلم</span>
                </button>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept=".srt,.ass,.ssa,.vtt"
                  multiple
                  style={{ display: "none" }}
                />
              </div>
            </div>

            {/* ========== گروه دوم: ابزارهای پیشرفته ========== */}
            <div className="action-group secondary-group">
              {!isMobile && <div className="group-label">ابزارهای پیشرفته</div>}
              <div className="selector-actions secondary-actions">
                <button
                  className="action-button comparison-button"
                  onClick={() => {
                    onClose();
                    handleComparisonClick();
                  }}
                >
                  <span className="action-icon">⚖️</span>
                  <span className="action-text">مقایسه و انتخاب زیرنویس</span>
                </button>

                <button
                  className="action-button collection-button"
                  onClick={() => {
                    onClose();
                    handleCollectionSearchClick();
                  }}
                >
                  <span className="action-icon">📚</span>
                  <span className="action-text">جستجوی دو زیرنویس</span>
                </button>
              </div>
            </div>

            {/* ========== بخش ۱: زیرنویس‌های اینترنت ========== */}
            {subtitleHistory.length > 0 && (
              <div className="history-section">
                <div className="history-header">
                  <span className="history-title">
                    {isMobile ? "🌐 اینترنت" : "🌐 بارگزاری شده از اینترنت"}
                  </span>
                  <span className="history-count">
                    {subtitleHistory.length}
                  </span>
                </div>

                <div className="history-list">
                  {subtitleHistory.map((sub, index) =>
                    renderSubtitleItem(sub, true),
                  )}
                </div>
              </div>
            )}

            {/* ========== بخش ۲: زیرنویس‌های فیلم ========== */}
            {embeddedSubtitles.length > 0 && (
              <div className="history-section">
                <div className="history-header">
                  <span className="history-title">
                    {isMobile ? "🎬 فیلم" : "🎬 استخراج شده از فیلم"}
                  </span>
                  <span className="history-count">
                    {embeddedSubtitles.length}
                  </span>
                </div>

                <div className="history-list">
                  {embeddedSubtitles.map((sub) => renderSubtitleItem(sub))}
                </div>
              </div>
            )}

            {/* ========== بخش ۳: زیرنویس‌های آپلودی ========== */}
            {uploadedSubtitles.length > 0 && (
              <div className="history-section">
                <div className="history-header">
                  <span className="history-title">
                    {isMobile ? "💾 دستگاه" : "💾 آپلود شده از دستگاه"}
                  </span>
                  <span className="history-count">
                    {uploadedSubtitles.length}
                  </span>
                </div>

                <div className="history-list">
                  {uploadedSubtitles.map((sub) => renderSubtitleItem(sub))}
                </div>
              </div>
            )}

            {/* پیام خالی */}
            {totalCount === 0 && (
              <div className="empty-history">
                <span className="empty-icon">📋</span>
                <span className="empty-text">زیرنویسی بارگزاری نشده</span>
                {!isMobile && (
                  <span className="empty-hint">
                    زیرنویس‌هایی که جستجو یا آپلود می‌کنید در اینجا نمایش داده
                    می‌شوند
                  </span>
                )}
              </div>
            )}
          </div>

          {/* فوتر */}
          {(selectedForDownload[1] || selectedForDownload[2]) && (
            <div className="selector-footer">
              <div className="selected-info">
                {selectedForDownload[1] && (
                  <span className="selected-badge">
                    {isMobile ? "۱:" : "زیرنویس ۱:"}{" "}
                    {getLanguageName(
                      selectedForDownload[1].subtitlesLanguage ||
                        selectedForDownload[1].language,
                    )}
                  </span>
                )}
                {selectedForDownload[2] && (
                  <span className="selected-badge">
                    {isMobile ? "۲:" : "زیرنویس ۲:"}{" "}
                    {getLanguageName(
                      selectedForDownload[2].subtitlesLanguage ||
                        selectedForDownload[2].language,
                    )}
                  </span>
                )}
              </div>

              <button
                className="download-btn"
                onClick={handleDownloadSelected}
                disabled={downloading}
              >
                {downloading ? (
                  <i className="fas fa-spinner fa-spin"></i>
                ) : (
                  <i className="fas fa-download"></i>
                )}
                {downloading
                  ? "در حال دریافت..."
                  : isMobile
                    ? "افزودن"
                    : "افزودن زیرنویس‌ها"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* مودال‌ها بدون تغییر */}
      {showSearchModal && (
        <SubtitleSearchModal
          onClose={handleSearchModalClose}
          onSuccess={() => handleSearchModalClose(true)}
        />
      )}

      {uploadModalFiles && (
        <SubtitleUploadModal
          files={uploadModalFiles}
          onClose={() => {
            setUploadModalFiles(null);
          }}
          onConfirm={handleUploadConfirm}
        />
      )}

      {showEmbeddedModal && (
        <EmbeddedSubtitlesModal
          onClose={() => {
            setShowEmbeddedModal(false);
            onClose();
          }}
        />
      )}
    </>,
    getPortalContainer(isIOS, isFullscreen),
  );
};

export default SubtitleSelector;
